import { useQuery } from '@apollo/client/react';
import { lazy, Suspense } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import Breadcrumb from '../../components/Atoms/Breadcrumb';
import CapacitorBreadcrumb from '../../components/Atoms/CapacitorBreadcrumb';
import CapacitorNotificationsHandler from '../../components/Atoms/CapacitorNotificationsHandler';
import Meta from '../../components/Atoms/Meta';
import WebPushNotificationsHandler from '../../components/Atoms/WebPushNotificationsHandler';
import Container from '../../components/Layout/Container';
import Footer from '../../components/Layout/Footer';
import Header from '../../components/Layout/Header';
import { CurrentUserProvider } from '../../contexts/CurrentUserContext';
import { getContentLanguagesValues, getInterfaceLanguagesValues, useLanguage } from '../../contexts/LanguageContext';
import { useWindow } from '../../contexts/WindowContext';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import { useMeta } from '../../hooks/useMeta';
import { useUserStore } from '../../hooks/useUserStore';
import { ROUTES } from '../../routes';
import LanguagesSelectorWindow from '../Windows/LanguagesSelectorWindow';
import SignOutWindow from '../Windows/SignOutWindow';

import GetPinnedContentsQuery from './GetPinnedContentsQuery.graphql';

const Banner = lazy(() => import('./Banner'));
const LayoutAsync = lazy(() => import('./LayoutAsync'));

const DynamicHeader = (props) => {
  const meta = useMeta();

  return <Header
    {...props}
    history={meta?.history}
    actions={meta?.actions}
  />
}

const DynamicBreadcrumb = (props) => {
  const meta = useMeta();

  return <Breadcrumb
    {...props}
    history={meta?.history}
    actions={meta?.actions}
  />
}

const CapacitorBackButtonHandler = (props) => {
  const meta = useMeta();

  return <CapacitorBreadcrumb
    {...props}
    history={meta?.history}
  />
}

const DynamicMeta = (props) => {
  const meta = useMeta();

  return <Meta
    {...props}
    title={meta?.title}
  />
}


const Layout = ({ children }) => {
  const {
    currentUser,
    allowedActions,
    unreadNotificationsCount,
    unreadChatsCount,
    changeLanguage,
    signIn,
    signUp,
    signOut,
    resetPassword,
    obtainBadge,
    refetch,
  } = useUserStore();
  const { actions: windowActions } = useWindow();
  const navigate = useNavigate();
  const { currentContentLanguages, currentInterfaceLanguage } = useLanguage();
  const location = useLocation();
  const isKeyboardOpened = useKeyboardOpen();

  const route = ROUTES[Object.keys(ROUTES).find(r => matchPath(ROUTES[r].path(), location.pathname)) || null] || null;

  const { data } = useQuery(GetPinnedContentsQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      filter: {
        isPublished: true,
        language: currentInterfaceLanguage,
        showOnPages: true,
      }
    }
  });

  const handleSignOut = async () => {
    windowActions.open({
      component: () => (
        <SignOutWindow
          onSignOut={async () => {
            windowActions.close();
            await signOut();
            navigate('/');
          }}
          onCancel={() => windowActions.close()}
        />
      ),
    });
  };

  const handleLanguagesChangeSubmit = async (data) => {
    windowActions.close();
    await changeLanguage(data.interfaceLanguage, data.contentLanguages)
  };

  const handleLanguagesChange = () => {
    windowActions.open({
      component: () => (
        <LanguagesSelectorWindow
          contentLanguages={getContentLanguagesValues()}
          interfaceLanguages={getInterfaceLanguagesValues()}
          currentContentLanguages={currentContentLanguages}
          currentInterfaceLanguage={currentInterfaceLanguage}
          onSubmit={handleLanguagesChangeSubmit}
        />
      ),
    });
  };

  const contests = data?.contests?.edges?.map(({ node }) => node);
  const contestsToJudge = data?.contestsToJudge?.edges?.map(({ node }) => node);
  const easterGameScore = currentUser?.easterGameScore || null;

  return (<>
    <CurrentUserProvider
      currentUser={currentUser}
      allowedActions={allowedActions}
      signIn={signIn}
      signUp={signUp}
      signOut={signOut}
      resetPassword={resetPassword}
      refetch={refetch}
      obtainBadge={obtainBadge}
    >
      <DynamicHeader
        easterGameScore={easterGameScore}
        contestsToJudge={contestsToJudge}
        contests={contests}
        user={currentUser}
        allowedActions={allowedActions}
        unreadChats={unreadChatsCount}
        unreadNotifications={unreadNotificationsCount}
        onSignOut={handleSignOut}
        onLanguageChange={handleLanguagesChange}
        isKeyboardOpened={isKeyboardOpened}
        isHomepage={route?.path() === '/'}
      />
      <Container isKeyboardOpened={isKeyboardOpened}>
        <Suspense fallback={null}>
          <Banner currentUser={currentUser} allowedActions={allowedActions} banners={data?.banners?.edges?.map(e => e?.node) || []} />
        </Suspense>
        {!route?.disableDesktopBreadcrumb && <DynamicBreadcrumb />}
        {children}
      </Container>
    </CurrentUserProvider>
    <Footer user={currentUser} onObtainBadge={obtainBadge} />
    <Suspense fallback={null}>
      <LayoutAsync currentUser={currentUser} onObtainBadge={obtainBadge} />
    </Suspense>
    <CapacitorNotificationsHandler enabled={!!currentUser} />
    <WebPushNotificationsHandler enabled={!!currentUser} />
    <DynamicMeta notificationCount={unreadChatsCount + unreadNotificationsCount} />
    <CapacitorBackButtonHandler />
  </>
  );
};

export default Layout;
